import React from 'react';
import { Bitcoin, Hexagon, DollarSign, Coins } from 'lucide-react';

/**
 * Supported wallet keys (currency + network combination)
 */
export type WalletKey =
  | 'BTC'
  | 'ETH'
  | 'USDT (ERC20)'
  | 'USDT (TRC20)'
  | 'USDC (ERC20)';

/**
 * Configuration for wallet display
 */
export interface WalletDisplayConfig {
  name: string;
  shortName: string;
  icon: React.ReactNode;
  className: string;
  iconBgClassName: string;
  decimals: number;
}

/**
 * Wallet display configuration by currency and network
 * Used for consistent styling and formatting across the app
 */
export const WALLET_CONFIG: Record<WalletKey, WalletDisplayConfig> = {
  BTC: {
    name: 'Bitcoin',
    shortName: 'BTC',
    icon: React.createElement(Bitcoin, {
      className: 'w-6 h-6 text-orange-500',
    }),
    className:
      'bg-linear-to-br from-orange-500/20 to-orange-500/5 border-orange-500/20',
    iconBgClassName: 'bg-orange-500/20',
    decimals: 8,
  },
  ETH: {
    name: 'Ethereum',
    shortName: 'ETH',
    icon: React.createElement(Hexagon, { className: 'w-6 h-6 text-blue-500' }),
    className:
      'bg-linear-to-br from-blue-500/20 to-blue-500/5 border-blue-500/20',
    iconBgClassName: 'bg-blue-500/20',
    decimals: 6,
  },
  'USDT (ERC20)': {
    name: 'Tether (ERC20)',
    shortName: 'USDT',
    icon: React.createElement(DollarSign, {
      className: 'w-6 h-6 text-green-500',
    }),
    className:
      'bg-linear-to-br from-green-500/20 to-green-500/5 border-green-500/20',
    iconBgClassName: 'bg-green-500/20',
    decimals: 2,
  },
  'USDT (TRC20)': {
    name: 'Tether (TRC20)',
    shortName: 'USDT',
    icon: React.createElement(DollarSign, {
      className: 'w-6 h-6 text-green-500',
    }),
    className:
      'bg-linear-to-br from-green-500/20 to-green-500/5 border-green-500/20',
    iconBgClassName: 'bg-green-500/20',
    decimals: 2,
  },
  'USDC (ERC20)': {
    name: 'USDC (ERC20)',
    shortName: 'USDC',
    icon: React.createElement(DollarSign, {
      className: 'w-6 h-6 text-blue-400',
    }),
    className:
      'bg-linear-to-br from-blue-400/20 to-blue-400/5 border-blue-400/20',
    iconBgClassName: 'bg-blue-400/20',
    decimals: 2,
  },
};

/**
 * Default configuration for unknown currencies
 */
export const DEFAULT_WALLET_CONFIG: WalletDisplayConfig = {
  name: 'Unknown',
  shortName: 'UNKNOWN',
  icon: React.createElement(Coins, { className: 'w-6 h-6 text-gray-500' }),
  className:
    'bg-linear-to-br from-gray-500/20 to-gray-500/5 border-gray-500/20',
  iconBgClassName: 'bg-gray-500/20',
  decimals: 8,
};
